import { useEffect, useState } from 'react';
import { useNotification } from 'src/hooks/useNotification';
import { Controller, useForm } from 'react-hook-form';
import { Button } from 'src/components/ui/button';
import { useUpdateKnowledgeBaseProjectsMutation } from 'src/services/knowledgeBaseService';
import { Checkbox } from 'src/components/ui/checkbox';
import { configurationService, useGetAllProjectsQuery } from 'src/services';
import { Separator } from 'src/components/ui/separator';
import { useAppDispatch } from 'src/store';
import type { KnowledgeBase } from 'src/services/knowledgeBaseService';
import type { ConfigurationSettingsPage } from 'src/services/configurationService';
import type { Project } from 'src/services/projectsService';
import { getApiVersion } from 'src/utils/apiVersion';
import { API_VERSION } from 'src/constants/api-routes';

// A project the knowledge base is assigned to.
interface ProjectItem {
  project_id: number;
}

export const AddProjectsAiKnowledgeBaseForm = ({
  close = () => {},
  knowledgeBase,
}: {
  close?: () => void;
  knowledgeBase: KnowledgeBase;
}) => {
  const version = getApiVersion();
  const isV1 = version === API_VERSION.V1;
  const notification = useNotification();
  const dispatch = useAppDispatch();
  const [isCheckingConfigurations, setIsCheckingConfigurations] = useState(false);
  const [excludedProjects, setExcludedProjects] = useState<Project[]>([]);

  const { data: projects } = useGetAllProjectsQuery(undefined);

  const [
    updateKnowledgeBaseProjects,
    {
      isLoading: updateKnowledgeBaseProjectsIsLoading,
      isSuccess: updateKnowledgeBaseProjectsIsSuccess,
    },
  ] = useUpdateKnowledgeBaseProjectsMutation();

  useEffect(() => {
    if (updateKnowledgeBaseProjectsIsSuccess) {
      // Keep the popup open so the excluded projects message stays visible.
      if (!excludedProjects.length) {
        close();
      }
      notification.success('Knowledge Base projects was successfully updated!');
    }
  }, [updateKnowledgeBaseProjectsIsLoading, updateKnowledgeBaseProjectsIsSuccess]);

  const {
    handleSubmit,
    control,
    formState: { isDirty },
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      project_items:
        knowledgeBase?.projects.map((item) => {
          return { project_id: item.id };
        }) || [],
    },
  });

  const submit = async (data: { project_items: ProjectItem[] }) => {
    const projectIds = data.project_items.map((item) => item.project_id);

    if (isV1) {
      return updateKnowledgeBaseProjects({
        knowledge_base_id: knowledgeBase.id,
        projects_ids: projectIds,
      });
    }

    let settingsByProject: ConfigurationSettingsPage[];
    setIsCheckingConfigurations(true);
    try {
      settingsByProject = await Promise.all(
        projectIds.map((id) =>
          dispatch(
            configurationService.endpoints.getAllConfigurationSettings.initiate(
              { id },
              { subscribe: false, forceRefetch: true },
            ),
          ).unwrap(),
        ),
      );
    } catch {
      notification.error('Failed to check project configurations. Please try again.');
      return;
    } finally {
      setIsCheckingConfigurations(false);
    }

    // Projects whose deployed configuration is custom can't use a knowledge base.
    const excludedIds = new Set(
      projectIds.filter(
        (_, index) => settingsByProject[index].items.find((item) => item.is_deployed)?.is_custom,
      ),
    );
    setExcludedProjects(projects?.filter((project) => excludedIds.has(project.id)) || []);

    updateKnowledgeBaseProjects({
      knowledge_base_id: knowledgeBase.id,
      projects_ids: projectIds.filter((id) => !excludedIds.has(id)),
    });
  };

  const activateSubscriptionCheckBoxHandler = (
    checked: boolean | 'indeterminate',
    onChange: (items: ProjectItem[]) => void,
    value: ProjectItem[],
    project: Pick<Project, 'id'>,
  ) => {
    if (checked) {
      return onChange([...value, { project_id: Number(project.id) }]);
    }

    onChange(value?.filter((value) => value.project_id !== project.id));
  };

  const projectsByType = projects?.reduce(
    (acc, project) => {
      if (!acc[project?.project_type]) {
        acc[project.project_type] = [];
      }
      acc[project?.project_type].push(project);
      return acc;
    },
    { agent: [], bi: [], customer: [] },
  );

  const clearExcludedProjects = () => {
    const newValue = checkedProjects.filter(
      ({ project_id }) => !excludedProjects.some((project) => project.id === project_id),
    );
    setValue('project_items', newValue);
    setExcludedProjects([]);
  };

  const checkedProjects = watch('project_items');

  // Unchecking every project of a saved selection disconnects them.
  const assignLabel = !isDirty || checkedProjects.length ? 'Assign' : 'Disconnect';
  const isSubmitting = isCheckingConfigurations || updateKnowledgeBaseProjectsIsLoading;

  if (excludedProjects.length) {
    return (
      <div className="flex flex-col text-center">
        <output className="block text-base text-ebony font-medium">
          Knowledge base can&apos;t be assigned to{' '}
          <b>{excludedProjects.map((project) => project.name).join(', ')}</b>, because{' '}
          {excludedProjects.length > 1 ? 'they have' : 'it has'} custom configurations.
        </output>
        <output className="block text-base text-ebony font-medium">
          Please contact your Account Manager.
        </output>
        <div className="flex justify-center mt-5">
          <Button
            variant={'outline'}
            type="button"
            className="min-w-24"
            onClick={clearExcludedProjects}
          >
            Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form
      id="add-subscription-plan-form"
      onSubmit={handleSubmit(submit)}
      className={'flex flex-col'}
    >
      <div className={'input-container flex flex-col relative max-h-[60vh] overflow-auto'}>
        <Controller
          control={control}
          name={'project_items'}
          render={({ field: { onChange, value } }) => (
            <>
              {[...projectsByType.customer, ...projectsByType.bi].map((project, index, array) => {
                return (
                  <div key={project.id} className="flex w-full flex-col">
                    <label
                      htmlFor={`sub-${project.id}`}
                      className="flex items-center space-x-2 py-[15px] cursor-pointer"
                    >
                      <Checkbox
                        id={`sub-${project.id}`}
                        checked={value?.some((el) => el.project_id === project.id)}
                        onCheckedChange={(checked) => {
                          activateSubscriptionCheckBoxHandler(checked, onChange, value, project);
                        }}
                      />
                      <span className="text-base text-ebony opacity-50 cursor-pointer font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        {project?.name}
                      </span>
                    </label>
                    {index < array.length - 1 && <Separator />}
                  </div>
                );
              })}
            </>
          )}
        />
      </div>
      <div className="flex justify-center mt-5">
        <div className="flex gap-2.5">
          <Button variant="outline" type="button" className="min-w-24" onClick={close}>
            Cancel
          </Button>
          <Button disabled={!isDirty || isSubmitting} type="submit" className="min-w-24">
            {isSubmitting ? 'Loading...' : assignLabel}
          </Button>
        </div>
      </div>
    </form>
  );
};
